import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whitelistService } from '../services/whitelistService';
import { WhitelistEntry } from '../types/api';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import { AddWhitelistDialog } from '../components/Whitelist/AddWhitelistDialog';
import { EditWhitelistDialog } from '../components/Whitelist/EditWhitelistDialog';

export const WhitelistPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [search, setSearch] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<WhitelistEntry | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<WhitelistEntry | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['whitelist', paginationModel.page + 1, paginationModel.pageSize, search],
    queryFn: () =>
      whitelistService.getAll({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
      }),
    placeholderData: (previousData) => previousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => whitelistService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whitelist'] });
      setDeleteConfirmOpen(false);
      setEntryToDelete(null);
    },
  });

  const handleEdit = (entry: WhitelistEntry) => {
    setSelectedEntry(entry);
    setEditDialogOpen(true);
  };

  const handleDelete = (entry: WhitelistEntry) => {
    setEntryToDelete(entry);
    setDeleteConfirmOpen(true);
  };

  const columns: GridColDef<WhitelistEntry>[] = [
    {
      field: 'ipAddress',
      headerName: 'IP/Prefix',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.row.ipAddress || params.row.ipPrefix}
        </Typography>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      width: 150,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'addedAt',
      headerName: 'Added',
      width: 170,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), 'MMM dd, yyyy HH:mm') : '-',
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      width: 150,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Whitelist Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Entry
        </Button>
      </Box>

      <Stack direction="row" spacing={2} mb={3}>
        <Chip label={`Total Entries: ${data?.totalCount || 0}`} color="primary" />
        <Chip
          label={`Active: ${data?.data.filter((e) => e.isActive).length || 0}`}
          color="success"
        />
      </Stack>

      <Box mb={2}>
        <TextField
          fullWidth
          placeholder="Search by IP address or prefix..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          loading={isLoading}
          disableRowSelectionOnClick
        />
      </Box>

      <AddWhitelistDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} />

      {selectedEntry && (
        <EditWhitelistDialog
          open={editDialogOpen}
          entry={selectedEntry}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedEntry(null);
          }}
        />
      )}

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this whitelist entry?
          </Alert>
          <Typography variant="body2" fontFamily="monospace">
            {entryToDelete?.ipAddress || entryToDelete?.ipPrefix}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => entryToDelete && deleteMutation.mutate(entryToDelete.id)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
